// React & Hooks
import React, { useEffect, useState } from "react";

// Third-party Libraries
import Papa from "papaparse";
import { useDropzone } from "react-dropzone";
import { useSelector } from "react-redux";

// Common / UI Components
import { Toast } from "../common/toast/toast";
import DeletePopup from "./delete-popup";

// API / Config
import { deleteKnowledgeBaseFile } from "../../js/api/trials-file-upload";
const FileUpload = ({ files, setFiles, apiFiles, trialID, fetchFiles }) => {
  const [toasts, setToasts] = useState([]);
  const [deletePopup, setDeletePopup] = useState({
    visible: false,
    fileName: "",
  });

  const [header, setHeader] = useState([]);
  const [allFiles, setAllFiles] = useState([]); // combined state
  const trialNamesList = useSelector((state) => state.trails_name.data || []);

  // Combine files after API call or whenever files change
  useEffect(() => {
    setAllFiles([...apiFiles, ...files]);
  }, [apiFiles, files]);

  // Parse CSV file and extract headers
  const handleFileUpload = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        setHeader(result.meta.fields);
      },
      error: (error) => {
        console.error("Error parsing CSV file:", error);
        addToast("Error parsing CSV file", "error");
      },
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    multiple: true,
    disabled: false,
    onDrop: (acceptedFiles) => {
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      const filteredFiles = acceptedFiles.filter((file) =>
        validTypes.includes(file.type)
      );

      if (filteredFiles.length !== acceptedFiles.length) {
        addToast("Some files were invalid and not uploaded.", "error");
      }

      // Filter out files with the same name
      const existingFileNames = new Set([
        ...files.map((f) => f.name),
        ...apiFiles.map((f) => f.fileName),
      ]);

      const uniqueFiles = filteredFiles.filter(
        (file) => !existingFileNames.has(file.name)
      );

      if (uniqueFiles.length !== filteredFiles.length) {
        if (acceptedFiles.length < 2) {
          addToast(
            `The file '${acceptedFiles[0].name}' already exists. Please upload a different file.`,
            "error"
          );
        } else {
          addToast(
            "Some files were skipped because they already exist.",
            "error"
          );
        }
      }

      setFiles((prevFiles) => [...prevFiles, ...uniqueFiles]);
    },
  });

  // Remove a file from files array
  const removeFile = async () => {
    if (deletePopup.fileID) {
      // API delete
      try {
        await deleteKnowledgeBaseFile(trialID, deletePopup.fileID).then(() => {
          setFiles([]);
          fetchFiles().then(() => {});

          // Update API files after deletion
          setAllFiles(apiFiles.filter((f) => f.fileID !== deletePopup.fileID));
          setDeletePopup({ visible: false, fileName: "", fileID: null });
          addToast("File deleted successfully", "success");
        }); // trialId should be available in props or state
      } catch (error) {
        addToast("Failed to delete file from server.", "error");
      }
    } else {
      // Local delete
      setFiles(files.filter((f) => f.name !== deletePopup.fileName));
      setDeletePopup({ visible: false, fileName: "" });
      addToast("File deleted successfully", "success");
    }
  };

  // Toast handlers
  const addToast = (message, type) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => removeToast(id), 3000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const confirmRemoveFile = (data) => {
    if (data.fileId) {
      setDeletePopup({
        visible: true,
        fileName: data.fileName,
        fileID: data.fileId,
      });
    } else {
      setDeletePopup({ visible: true, fileName: data.fileName });
    }
  };

  return (
    <>
      {/* Toast messages */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          toast={toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {/* Dropzone */}
      <div className="flex items-center justify-center w-full pb-5">
        <div
          {...getRootProps()}
          className={`flex flex-col items-center justify-center w-full border-2 h-[176px] border-dashed rounded-lg ${
            // files.length > 0
            // ? "border-gray-300 bg-gray-200 cursor-not-allowed"
            // :
            "border-gray-300 bg-gray-50 cursor-pointer hover:bg-gray-100"
          } border-dashed rounded-lg`}
        >
          <div className="flex flex-col items-center justify-center pt-5">
            <svg
              width="56"
              height="60"
              className="mb-[16px]"
              viewBox="0 0 56 60"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M45.5 13C45.5 12.6685 45.3683 12.3505 45.1339 12.1161L33.3839 0.366116C33.1495 0.131696 32.8315 0 32.5 0H1.75C1.05964 0 0.5 0.559644 0.5 1.25V58.75C0.5 59.4404 1.05964 60 1.75 60H36.3423C35.3345 59.2903 34.4263 58.4486 33.6429 57.5H3V2.5H30.5V13.75C30.5 14.4404 31.0596 15 31.75 15H43V35.0561C43.4117 35.019 43.8286 35 44.25 35C44.6714 35 45.0883 35.019 45.5 35.0561V13ZM36.75 25C37.4404 25 38 24.4404 38 23.75C38 23.0596 37.4404 22.5 36.75 22.5H9.25C8.55964 22.5 8 23.0596 8 23.75C8 24.4404 8.55964 25 9.25 25H36.75ZM26.75 32.5C27.4404 32.5 28 31.9404 28 31.25C28 30.5596 27.4404 30 26.75 30H9.25C8.55964 30 8 30.5596 8 31.25C8 31.9404 8.55964 32.5 9.25 32.5H26.75ZM31.75 40C32.4404 40 33 39.4404 33 38.75C33 38.0596 32.4404 37.5 31.75 37.5H9.25C8.55964 37.5 8 38.0596 8 38.75C8 39.4404 8.55964 40 9.25 40H31.75ZM21.75 47.5C22.4404 47.5 23 46.9404 23 46.25C23 45.5596 22.4404 45 21.75 45H9.25C8.55964 45 8 45.5596 8 46.25C8 46.9404 8.55964 47.5 9.25 47.5H21.75Z"
                fill="url(#paint0_linear_1652_131163)"
                fillOpacity="0.8"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M45.5 50V53.75C45.5 54.4404 44.9404 55 44.25 55C43.5596 55 43 54.4404 43 53.75V50H39.25C38.5596 50 38 49.4404 38 48.75C38 48.0596 38.5596 47.5 39.25 47.5H43V43.75C43 43.0596 43.5596 42.5 44.25 42.5C44.9404 42.5 45.5 43.0596 45.5 43.75V47.5H49.25C49.9404 47.5 50.5 48.0596 50.5 48.75C50.5 49.4404 49.9404 50 49.25 50H45.5ZM44.25 60C50.4632 60 55.5 54.9632 55.5 48.75C55.5 42.5368 50.4632 37.5 44.25 37.5C38.0368 37.5 33 42.5368 33 48.75C33 54.9632 38.0368 60 44.25 60Z"
                fill="url(#paint1_linear_1652_131163)"
                fillOpacity="0.8"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_1652_131163"
                  x1="23"
                  y1="0"
                  x2="23"
                  y2="60"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#3338BA" />
                  <stop offset="1" stop-color="#630695" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_1652_131163"
                  x1="44.25"
                  y1="37.5"
                  x2="44.25"
                  y2="60"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#3338BA" />
                  <stop offset="1" stop-color="#630695" />
                </linearGradient>
              </defs>
            </svg>
            <p className="mb-2 text-sm text-gray-500">
              {/* {files.length > 0
                ? "Files Uploaded"
                : 
                (
                  <div className="font-mont">
                    <span className="font-mont text-[#245CEA] font-[600]">Select </span>
                    <span className="text-[#373737]">or drag and drop files here</span>
                  </div>
                )} */}

              <div className="font-mont">
                <span className="font-mont text-[#245CEA] font-[600]">
                  Select{" "}
                </span>
                <span className="text-[#373737]">
                  or drag and drop files here
                </span>
              </div>
            </p>
            <span className="text-[#202020] text-sm">
              (Supported Format: PDF, DOC, DOCX)
            </span>
          </div>
          <input {...getInputProps()} />
        </div>
      </div>

      {/* Uploaded files list */}
      {allFiles.length > 0 &&
        allFiles.map((file, index) => (
          <div
            key={index}
            className="mb-5 flex items-center border  p-3 rounded-lg bg-gray-50 max-h-screen w-max flex-wrap"
          >
            <div className="mr-4">
              <svg
                width="21"
                height="24"
                viewBox="0 0 21 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.383 7.26461C17.0188 7.43897 18.664 7.50646 20.3081 7.46521L20.3043 7.45865L20.3034 7.45959C18.362 4.52731 15.8554 2.01331 12.9296 0.0644531C12.8865 1.7171 12.954 3.37062 13.1302 5.01494C13.2558 6.09858 14.2973 7.14006 15.3828 7.26473L15.383 7.26461Z"
                  fill="#245CEA"
                />
                <path
                  d="M10.1826 9.47642C10.1967 9.33112 10.147 9.18583 10.0458 9.07991C9.94451 8.97398 9.80203 8.9168 9.65579 8.92336C9.50299 8.92992 9.36143 9.00491 9.2705 9.12772C9.17957 9.2477 9.14864 9.40331 9.18707 9.54955C9.353 10.201 9.58736 10.8329 9.88733 11.4356H9.88639C10.0251 10.7897 10.1245 10.1354 10.1836 9.47643L10.1826 9.47642Z"
                  fill="#245CEA"
                />
                <path
                  d="M4.81575 19.3501C4.782 19.5939 4.91511 19.8301 5.1401 19.9285C5.36602 20.0279 5.62944 19.9641 5.78504 19.7748C6.27812 19.173 6.7365 18.544 7.15836 17.8896C6.48342 18.1952 5.78974 18.5458 5.08481 18.9489C4.9367 19.0333 4.83732 19.1814 4.81575 19.3501Z"
                  fill="#245CEA"
                />
                <path
                  d="M10.2853 14.1338C10.0818 14.7 9.85782 15.2409 9.62158 15.7527C10.2197 15.5652 10.7933 15.4152 11.3333 15.2887C10.9611 14.925 10.6106 14.5388 10.2853 14.1338Z"
                  fill="#245CEA"
                />
                <path
                  d="M20.353 8.77753C20.0249 8.78597 19.6996 8.79066 19.3753 8.79066L19.3762 8.79159C17.9954 8.79159 16.6165 8.71754 15.2441 8.5713H15.2328C14.4154 8.47756 13.6111 8.07073 12.9699 7.4286C12.3287 6.78741 11.9219 5.98312 11.8272 5.16569V5.15444L11.8254 5.15257C11.6435 3.44741 11.5741 1.73193 11.6173 0.0175336C9.03849 -0.0452732 6.4578 0.0606551 3.89294 0.335311C2.17937 0.534041 0.536165 2.17639 0.337465 3.88985C-0.112488 8.09497 -0.112488 15.9009 0.337465 20.107C0.536194 21.8206 2.17947 23.4638 3.89294 23.6625C8.08507 24.1125 12.3127 24.1125 16.5048 23.6625C18.2184 23.4638 19.8616 21.8205 20.0603 20.107C20.3668 17.2415 20.4636 12.7072 20.353 8.77753Z"
                  fill="#245CEA"
                />
                <path
                  d="M15.1602 15.8618C14.8115 15.8806 14.4009 15.9143 13.9341 15.9696C14.2603 16.1937 14.609 16.4121 14.9802 16.6258H14.9793C15.0871 16.6886 15.2174 16.6971 15.3318 16.6483C15.4518 16.5996 15.5408 16.4965 15.5727 16.3718C15.6046 16.2471 15.5764 16.114 15.4958 16.0128C15.4152 15.9115 15.2896 15.8553 15.1602 15.8618Z"
                  fill="#245CEA"
                />
              </svg>
            </div>
            <div className="text-[14px] text-[##373737] font-mont font-[500]  truncate">
              {file.name || file.fileName}
            </div>
            <button
              onClick={() =>
                confirmRemoveFile({
                  fileName: file.name || file.fileName,
                  fileId: file.fileID,
                })
              }
              className="text-red-600 text-sm hover:underline ml-3"
            >
              <svg
                width="15"
                height="18"
                viewBox="0 0 15 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.8281 9.6499L13.4224 9.7326L12.8281 9.6499ZM12.6095 11.2208L13.2038 11.3035V11.3035L12.6095 11.2208ZM2.18475 11.2208L2.77902 11.1381L2.18475 11.2208ZM1.96614 9.64991L1.37186 9.73261L1.96614 9.64991ZM5.05017 17.1144L4.8169 17.6671L5.05017 17.1144ZM2.79304 14.4673L3.35623 14.2603L2.79304 14.4673ZM12.0012 14.4673L12.5644 14.6742L12.0012 14.4673ZM9.7441 17.1144L9.51082 16.5616H9.51082L9.7441 17.1144ZM2.16111 6.44357C2.12977 6.11369 1.83694 5.87167 1.50705 5.90302C1.17716 5.93436 0.935149 6.22719 0.966492 6.55708L2.16111 6.44357ZM13.8278 6.55708C13.8591 6.22719 13.6171 5.93436 13.2872 5.90302C12.9573 5.87167 12.6645 6.11369 12.6332 6.44357L13.8278 6.55708ZM14.0638 5.43366C14.3952 5.43366 14.6638 5.16503 14.6638 4.83366C14.6638 4.50229 14.3952 4.23366 14.0638 4.23366V5.43366ZM0.730469 4.23366C0.399098 4.23366 0.130469 4.50229 0.130469 4.83366C0.130469 5.16503 0.399098 5.43366 0.730469 5.43366V4.23366ZM5.13047 14.0003C5.13047 14.3317 5.3991 14.6003 5.73047 14.6003C6.06184 14.6003 6.33047 14.3317 6.33047 14.0003H5.13047ZM6.33047 7.33366C6.33047 7.00229 6.06184 6.73366 5.73047 6.73366C5.3991 6.73366 5.13047 7.00229 5.13047 7.33366H6.33047ZM8.4638 14.0003C8.4638 14.3317 8.73243 14.6003 9.0638 14.6003C9.39517 14.6003 9.6638 14.3317 9.6638 14.0003H8.4638ZM9.6638 7.33366C9.6638 7.00229 9.39517 6.73366 9.0638 6.73366C8.73243 6.73366 8.4638 7.00229 8.4638 7.33366H9.6638ZM10.7305 4.83366V5.43366H11.3305V4.83366H10.7305ZM4.0638 4.83366H3.4638V5.43366H4.0638V4.83366ZM12.2339 9.5672L12.0152 11.1381L13.2038 11.3035L13.4224 9.7326L12.2339 9.5672ZM2.77902 11.1381L2.56041 9.5672L1.37186 9.73261L1.59048 11.3035L2.77902 11.1381ZM7.39714 16.7337C6.12508 16.7337 5.66439 16.7223 5.28345 16.5616L4.8169 17.6671C5.47536 17.945 6.23287 17.9337 7.39714 17.9337V16.7337ZM1.59048 11.3035C1.82374 12.9796 1.95015 13.9129 2.22985 14.6742L3.35623 14.2603C3.13214 13.6504 3.02095 12.8764 2.77902 11.1381L1.59048 11.3035ZM5.28345 16.5616C4.50662 16.2337 3.78778 15.4349 3.35623 14.2603L2.22985 14.6742C2.74038 16.0637 3.65003 17.1747 4.8169 17.6671L5.28345 16.5616ZM12.0152 11.1381C11.7733 12.8764 11.6621 13.6504 11.438 14.2603L12.5644 14.6742C12.8441 13.9129 12.9705 12.9796 13.2038 11.3035L12.0152 11.1381ZM7.39714 17.9337C8.5614 17.9337 9.31891 17.945 9.97737 17.6671L9.51082 16.5616C9.12988 16.7223 8.66919 16.7337 7.39714 16.7337V17.9337ZM11.438 14.2603C11.0065 15.4349 10.2877 16.2337 9.51082 16.5616L9.97737 17.6671C11.1442 17.1747 12.0539 16.0637 12.5644 14.6742L11.438 14.2603ZM2.56041 9.5672C2.37534 8.23734 2.23702 7.24255 2.16111 6.44357L0.966492 6.55708C1.04545 7.3881 1.18821 8.41296 1.37186 9.73261L2.56041 9.5672ZM13.4224 9.7326C13.6061 8.41295 13.7488 7.3881 13.8278 6.55708L12.6332 6.44357C12.5572 7.24254 12.4189 8.23734 12.2339 9.5672L13.4224 9.7326ZM14.0638 4.23366H0.730469V5.43366H14.0638V4.23366ZM6.33047 14.0003V7.33366H5.13047V14.0003H6.33047ZM9.6638 14.0003V7.33366H8.4638V14.0003H9.6638ZM10.1305 4.00033V4.83366H11.3305V4.00033H10.1305ZM10.7305 4.23366H4.0638V5.43366H10.7305V4.23366ZM4.6638 4.83366V4.00033H3.4638V4.83366H4.6638ZM7.39714 1.26699C8.90671 1.26699 10.1305 2.49075 10.1305 4.00033H11.3305C11.3305 1.82801 9.56946 0.0669922 7.39714 0.0669922V1.26699ZM7.39714 0.0669922C5.22482 0.0669922 3.4638 1.82801 3.4638 4.00033H4.6638C4.6638 2.49075 5.88756 1.26699 7.39714 1.26699V0.0669922Z"
                  fill="#060427"
                />
              </svg>
            </button>
          </div>
        ))}
      {/* Delete Confirmation Popup */}
      {deletePopup.visible && (
        <DeletePopup
          visible={deletePopup.visible}
          fileName={deletePopup.fileName}
          onDelete={removeFile}
          onCancel={() => setDeletePopup({ visible: false, fileName: "" })}
        />
      )}
    </>
  );
};

export default FileUpload;
